import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { GripVertical, BarChart3, Loader2 } from 'lucide-react';

interface MonthData {
  month: string;
  invoiced: number;
  collected: number;
}

export function ChartWidget() {
  const [data, setData] = useState<MonthData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/analytics/revenue');
        const monthly = res.data?.data?.monthly;
        if (Array.isArray(monthly)) {
          setData(monthly.map((m: any) => ({
            month: m.month,
            invoiced: Number(m.invoiced) || 0,
            collected: Number(m.collected) || 0,
          })));
        }
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Drag handle */}
      <div className="widget-drag-handle flex cursor-grab items-center justify-between border-b border-gray-100 px-4 py-2 active:cursor-grabbing">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-cyan-100">
            <BarChart3 size={14} className="text-cyan-600" />
          </div>
          <span className="text-xs font-medium text-gray-500">Monthly Revenue Trend</span>
        </div>
        <GripVertical size={14} className="text-gray-300" />
      </div>

      {/* Chart */}
      <div className="flex-1 p-3">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 size={20} className="animate-spin text-gray-300" />
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">No revenue data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip
                formatter={(value: number) => formatINR(value, false)}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="invoiced" fill="#3b82f6" name="Invoiced" radius={[4, 4, 0, 0]} />
              <Bar dataKey="collected" fill="#22c55e" name="Collected" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
